import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useTrans from '@hooks/useTrans'
import { cn, dateTimeFormat, getUserType } from '@utils/safeTs'
import {
  PosizionePersonalizzazioneType,
  RootStateType,
  OrdineDetailType,
  UserType,
  PersonalizzazioneOrdineType,
} from '@utils/types'
import { Download, Larghezza, Altezza, Search, Upload } from '@components/icons'
import { Button, StatoOrdine, WImage, CronologiaButton, WLink } from '@components/atoms'
import MyAccountStatusLogModal from '@components/organisms/MyAccountStatusLogModal/MyAccountStatusLogModal'
import styles from './MyAccountPosizionePersonalizzazione.module.sass'

interface Props {
  ordine?: OrdineDetailType
  personalizzazione?: PersonalizzazioneOrdineType
  posizione?: PosizionePersonalizzazioneType
  wCheckout?: boolean
  userType?: UserType
  className?: string
}

const MyAccountPosizionePersonalizzazione = (props: Props) => {
  const { ordine, personalizzazione, posizione, wCheckout, userType, className = '' } = props

  const t = useTrans()
  const [logOpen, setLogOpen] = useState(false)
  const [mostraNotaOriginale, setMostraNotaOriginale] = useState(false)

  const isOrdineModificabile =
    !wCheckout &&
    ordine?.statoOrdineCalcolato !== 'ORDINE_ANNULLATO' &&
    ordine?.statoOrdineCalcolato !== 'ORDINE_EVASO' &&
    ordine?.statoOrdineCalcolato !== 'IN_ATTESA'
  const isBozzaVisibile = useMemo(
    () =>
      posizione.bozzaDigitaleImage?.url &&
      posizione?.statoPosizionePersonalizzazione !== 'BOZZA_DA_CARICARE',
    [posizione]
  )

  const dimensioni = [
    {
      key: 'larghezza',
      icon: <Larghezza aria-label={t('Larghezza')} />,
      value: posizione?.larghezza,
    },
    { key: 'altezza', icon: <Altezza aria-label={t('Altezza')} />, value: posizione?.altezza },
  ].filter((x) => !!x.value)
  const lastLog = posizione?.log?.length ? posizione.log[posizione.log.length - 1] : undefined
  useEffect(() => setMostraNotaOriginale(false), [lastLog?.id, lastLog?.note, lastLog?.noteTradotta])
  const lastLogNote = mostraNotaOriginale ? lastLog?.note : lastLog?.noteTradotta || lastLog?.note
  const previewSize = userType === 'cliente' ? 110 : 160
  const actionParams = {
    icon_pos: 'left',
    size: 'small',
    variant: 'primary',
  }
  const coloriValue = useMemo(
    () =>
      posizione?.numeroColoriPosizione?.nome
        ? posizione.numeroColoriPosizione?.fullColor
          ? posizione.numeroColoriPosizione?.nome
          : t(posizione?.numeroColoriPosizione?.nome === '1' ? '1 colore' : '{0} colori', [
              posizione?.numeroColoriPosizione?.numeroColori,
            ])
        : undefined,
    [posizione]
  )

  const renderImage = (type: 'bozza' | 'areaStampa') => (
    <WImage
      src={
        type === 'bozza' ? posizione.bozzaDigitaleImage?.url : posizione.areaStampa?.immagine?.url
      }
      maxWidth={previewSize}
      maxHeight={previewSize}
      objectFit="contain"
      alt={t(type === 'bozza' ? 'Bozza digitale {0}' : 'Anteprima posizione {0}', [
        posizione.posizione?.nome || '',
      ])}
    />
  )

  return posizione ? (
    <article
      className={cn(
        styles.root,
        styles[`root--${userType}`],
        styles[`root--${posizione.statoPosizionePersonalizzazione?.toLocaleLowerCase()}`],
        className
      )}
    >
      <div className={styles.previews}>
        <div className={styles.imageBox}>
          {renderImage(userType === 'cliente' && isBozzaVisibile ? 'bozza' : 'areaStampa')}
        </div>
        {userType !== 'cliente' &&
          (isBozzaVisibile ? (
            <div className={styles.imageBox}>{renderImage('bozza')}</div>
          ) : (
            posizione.statoPosizionePersonalizzazione === 'LOGO_DA_CARICARE' ?
            <WLink
              href={`${ordine.caricamentoLoghiUrl}#posizione_${posizione.id}`}
              aria-label={t('Carica logo {0}', [posizione.posizione?.nome])}
              className={styles.uploadBox}
              next={false}
            >
              <Upload aria-hidden />
            </WLink>
            : 
            <WLink
              href={`${ordine.caricamentoBozzeDigitaliUrl}#posizione_${posizione.id}`}
              aria-label={t('Carica bozza {0}', [posizione.posizione?.nome])}
              className={styles.uploadBox}
              next={false}
            >
              <Upload aria-hidden />
            </WLink>
          ))}
      </div>
      <div className={styles.main}>
        <div className={styles.details}>
          <div className={styles.specs}>
            {posizione.posizione?.nome ? (
              <div className={cn(styles.spec, styles['spec--position'])}>
                <div className={styles.specContent}>
                  {userType === 'cliente' ? (
                    <span className={styles.specOnlyValue}>{posizione.posizione?.nome}</span>
                  ) : (
                    <>
                      <span className={styles.specTitle}>{t('Posizione')}: </span>
                      <span className={styles.specValue}>{posizione.posizione?.nome}</span>
                    </>
                  )}
                </div>
              </div>
            ) : null}
            {!!posizione.tecnica?.nome || !!posizione?.numeroColoriPosizione?.numeroColori ? (
              <div className={styles.specWrap}>
                <div className={cn(styles.spec, styles['spec--colors'])}>
                  <div className={styles.specContent}>
                    {userType === 'cliente' ? (
                      <span className={styles.specOnlyValue}>
                        {[posizione.tecnica?.nome, coloriValue].filter((x) => !!x).join(' - ')}
                      </span>
                    ) : (
                      <>
                        <span className={styles.specTitle}>{t('Tecnica')}: </span>
                        <span className={styles.specValue}>
                          {[posizione.tecnica?.nome, coloriValue].filter((x) => !!x).join(' - ')}
                        </span>
                      </>
                    )}
                  </div>
                  {!!posizione.pantoni?.length && (
                    <div className={styles.colors}>
                      {posizione.pantoni.map((pantone, index) => (
                        <div key={pantone.id} className={styles.color}>
                          {t('Pantone {0}', [(index + 1).toString()])}: {pantone.pantone}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {userType === 'cliente' && personalizzazione?.confezionamento ? (
                  <div className={styles.spec}>
                    <div className={styles.specContent}>
                      <span className={styles.specTitle}>{t('Confezionamento')}</span>
                    </div>
                    <div className={styles.specDescription}>
                        {t(personalizzazione.confezionamentoRichiesto ? 'Sì' : 'No')}
                        {personalizzazione?.confezionamento?.nome ? <>{' '}-{' '}{personalizzazione.confezionamento.nome}</> : <></>}
                      </div>
                  </div>
                ) : null}
              </div>
            ) : null}
            {dimensioni?.length ? (
              <div className={cn(styles.spec, styles['spec--size'])}>
                <div className={styles.sizeContent}>
                  {userType !== 'cliente' && (
                    <span className={styles.sizesTitle}>{t('Dimensioni')}: </span>
                  )}
                  <div className={styles.sizes}>
                    {dimensioni.map((size) => (
                      <span key={size.key} className={styles.sizeItem}>
                        {size.icon}
                        <span className={styles.sizeItemValue}>{size.value} cm</span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
            {userType !== 'cliente' && personalizzazione?.confezionamento ? (
              <div className={cn(styles.spec, styles['spec--size'])}>
                <div className={styles.specContent}>
                  <span className={styles.specTitle}>{t('Confezionamento')}</span>
                  <div className={styles.specDescription}>
                    {t(personalizzazione.confezionamentoRichiesto ? 'Sì' : 'No')}
                    {personalizzazione?.confezionamento?.nome ? <>{' '}-{' '}{personalizzazione.confezionamento.nome}</> : <></>}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          {(!!lastLog || !!posizione.statoPosizionePersonalizzazione) && (
            <div className={styles.statusBox}>
              <StatoOrdine
                stato={
                  posizione.statoPosizionePersonalizzazione === 'BOZZA_DA_CARICARE' &&
                  posizione.log?.filter((l) => l.stato === 'BOZZA_RIFIUTATA')?.length &&
                  userType === 'cliente'
                    ? 'NUOVA_BOZZA_IN_LAVORAZIONE'
                    : posizione.statoPosizionePersonalizzazione
                }
                level={'posizione'}
                userType={userType}
              />
              <CronologiaButton onClick={() => setLogOpen(true)} />
              {!!lastLog && (
                <p className={styles.statusContent}>
                  <span className={styles.statusData}>{dateTimeFormat(lastLog.created)}</span>
                  {!!lastLogNote && (
                    <>
                      {' - '}
                      {lastLogNote}
                      {!!lastLog.noteTradotta && lastLog.noteTradotta !== lastLog.note && (
                        <span className={styles.translationNotice}>
                          {!mostraNotaOriginale && (
                            <>{t('Traduzione automatica generata da IA')}{' — '}</>
                          )}
                          <button
                            type="button"
                            className={styles.translationToggle}
                            onClick={() => setMostraNotaOriginale(value => !value)}
                          >
                            {t(mostraNotaOriginale ? 'Mostra traduzione' : 'Vedi originale')}
                          </button>
                        </span>
                      )}
                    </>
                  )}
                </p>
              )}
              {userType === 'cliente' &&
                posizione.statoPosizionePersonalizzazione === 'BOZZA_RIFIUTATA' &&
                ordine.statoOrdineCalcolato !== 'IN_ATTESA' && (
                  <div className={styles.statusMoreBox}>
                    <StatoOrdine
                      stato={'NUOVA_BOZZA_IN_LAVORAZIONE'}
                      level={'posizione'}
                      userType={userType}
                    />
                  </div>
                )}
            </div>
          )}
        </div>
        <div className={styles.actions}>
          {!wCheckout && isBozzaVisibile && (
            <Button
              label={t('Download bozza')}
              href={posizione.bozzaDigitale.url}
              target={'_blank'}
              icon={<Download />}
              {...actionParams}
            />
          )}
          {['admin', 'commerciale', 'cliente'].includes(userType) && (
            <>
              {isOrdineModificabile &&
                posizione.statoPosizionePersonalizzazione === 'BOZZA_INVIATA' && (
                  <Button
                    label={t('Valuta bozza')}
                    href={`${ordine.confermaBozzeDigitaliUrl}#posizione_${posizione.id}`}
                    icon={<Search />}
                    {...actionParams}
                  />
                )}
            </>
          )}
          {['admin', 'fornitore'].includes(userType) && isOrdineModificabile && (
            <>
              {['LOGO_DA_CARICARE'].includes(posizione.statoPosizionePersonalizzazione) && (
                <Button
                  label={t('Carica logo')}
                  href={`${ordine.caricamentoLoghiUrl}#posizione_${posizione.id}`}
                  icon={<Upload />}
                  {...actionParams}
                />
              )}
              {['BOZZA_DA_CARICARE', 'BOZZA_SALVATA', 'BOZZA_RIFIUTATA'].includes(
                  posizione.statoPosizionePersonalizzazione
                ) && (
                  <Button
                    label={t('Carica bozza')}
                    href={`${ordine.caricamentoBozzeDigitaliUrl}#posizione_${posizione.id}`}
                    icon={<Upload />}
                    {...actionParams}
                  />
                )}
            </>
          )}
        </div>
      </div>
      <MyAccountStatusLogModal
        id={`logmodal_${posizione.id}`}
        open={logOpen}
        onClose={() => setLogOpen(false)}
        posizione={posizione}
        userType={userType}
      />
    </article>
  ) : (
    <></>
  )
}

export default MyAccountPosizionePersonalizzazione
